import { Injectable } from '@angular/core';
import { ActionBackOfficeResponse } from '../../models/api.model';
import {
  CardTypeConfigDetail,
  CardTypeConfigListRow,
  CardTypeConfigTableDto,
  PlatformManagedCardType,
  SaveCardTypeConfigPayload,
} from '../../models/card-quota/card-type-config.model';

@Injectable({ providedIn: 'root' })
export class MapperCardTypeConfigService {
  mapTable(dto: CardTypeConfigTableDto): {
    items: CardTypeConfigListRow[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  } {
    const raw = dto as CardTypeConfigTableDto & Record<string, unknown>;
    const rows = (dto.data ?? raw['Data'] ?? []) as CardTypeConfigListRow[];
    return {
      items: rows.map((row) => this.mapListRow(row as CardTypeConfigListRow & Record<string, unknown>)),
      total: Number(dto.total ?? raw['Total'] ?? 0),
      page: Number(dto.page ?? raw['Page'] ?? 1),
      pageSize: Number(dto.pageSize ?? raw['PageSize'] ?? 20),
      totalPages: Number(dto.totalPages ?? raw['TotalPages'] ?? 0),
    };
  }

  mapListRow(row: CardTypeConfigListRow & Record<string, unknown>): CardTypeConfigListRow {
    return {
      id: Number(row.id ?? row['Id'] ?? 0),
      name: String(row.name ?? row['Name'] ?? ''),
      cardTypeId: Number(row.cardTypeId ?? row['CardTypeId'] ?? 0),
      cardTypeName: String(row.cardTypeName ?? row['CardTypeName'] ?? ''),
      subscriptionId: row.subscriptionId != null ? Number(row.subscriptionId ?? row['SubscriptionId']) : null,
      subscriptionName: String(row.subscriptionName ?? row['SubscriptionName'] ?? ''),
      cardScope: String(row.cardScope ?? row['CardScope'] ?? ''),
      managedByPlatform: Boolean(row.managedByPlatform ?? row['ManagedByPlatform'] ?? true),
      siteId: row.siteId != null ? Number(row.siteId ?? row['SiteId']) : null,
      siteName: String(row.siteName ?? row['SiteName'] ?? ''),
      enable: Boolean(row.enable ?? row['Enable'] ?? true),
    };
  }

  mapDetail(dto: CardTypeConfigDetail & Record<string, unknown>): CardTypeConfigDetail {
    return this.mapListRow(dto);
  }

  mapManagedTypes(rows: PlatformManagedCardType[]): PlatformManagedCardType[] {
    return (rows ?? []).map((row) => this.mapManagedType(row as PlatformManagedCardType & Record<string, unknown>));
  }

  mapManagedType(row: PlatformManagedCardType & Record<string, unknown>): PlatformManagedCardType {
    return {
      id: Number(row.id ?? row['Id'] ?? 0),
      cardTypeId: Number(row.cardTypeId ?? row['CardTypeId'] ?? 0),
      cardTypeName: String(row.cardTypeName ?? row['CardTypeName'] ?? ''),
      subscriptionId: row.subscriptionId != null ? Number(row.subscriptionId ?? row['SubscriptionId']) : null,
      subscriptionName: String(row.subscriptionName ?? row['SubscriptionName'] ?? ''),
      cardScope: String(row.cardScope ?? row['CardScope'] ?? ''),
      siteId: row.siteId != null ? Number(row.siteId ?? row['SiteId']) : null,
      siteName: String(row.siteName ?? row['SiteName'] ?? ''),
    };
  }

  mapToPayload(
    detail: CardTypeConfigDetail,
    userId: number,
    menuId: number,
    siteId: number
  ): SaveCardTypeConfigPayload {
    return {
      userId,
      menuId,
      id: detail.id > 0 ? detail.id : null,
      name: detail.name || null,
      cardTypeId: detail.cardTypeId,
      subscriptionId: detail.subscriptionId,
      cardScope: detail.cardScope,
      managedByPlatform: detail.managedByPlatform,
      siteId,
      enable: detail.enable,
    };
  }

  createEmptyDetail(siteId: number, siteName = ''): CardTypeConfigDetail {
    return {
      id: 0,
      name: '',
      cardTypeId: 0,
      cardTypeName: '',
      subscriptionId: null,
      subscriptionName: '',
      cardScope: 'free',
      managedByPlatform: true,
      siteId: siteId > 0 ? siteId : null,
      siteName,
      enable: true,
    };
  }

  scopeLabel(scope: string): string {
    const map: Record<string, string> = {
      free: 'บัตรฟรี',
      expiry: 'บัตรมีหมดอายุ',
      both: 'ทั้งสองประเภท',
    };
    return map[scope] ?? scope;
  }

  mapActionResponse(res: unknown): ActionBackOfficeResponse {
    const raw = (res ?? {}) as Record<string, unknown>;
    return {
      isSuccess: Boolean(raw['isSuccess'] ?? raw['IsSuccess'] ?? false),
      message: String(raw['message'] ?? raw['Message'] ?? ''),
      messageEn: String(raw['messageEn'] ?? raw['MessageEn'] ?? ''),
      code: String(raw['code'] ?? raw['Code'] ?? ''),
      IdReturn: Number(raw['IdReturn'] ?? raw['idReturn'] ?? 0),
    };
  }
}
